import { useState, useEffect, useCallback } from 'react';
import baseURL from '../Components/url';

export const useTallas = () => {
    const [tallas, setTallas] = useState([]);
    const [tiposTallas, setTiposTallas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Cargar tipos de tallas desde la API
    const cargarTiposTallas = useCallback(async () => {
        try {
            const response = await fetch(`${baseURL}/tiposTallasGet.php`);
            const data = await response.json();

            if (data.success) {
                const tiposActivos = data.tipos_tallas.filter(tipo => tipo.activo === 1 || tipo.activo === '1');
                setTiposTallas(tiposActivos);
                return tiposActivos;
            } else {
                throw new Error(data.error || 'Error al cargar tipos de tallas');
            }
        } catch (err) {
            console.error('Error cargando tipos de tallas:', err);
            return [];
        }
    }, []);

    // Cargar tallas desde la API
    const cargarTallas = useCallback(async (soloActivas = true, idTipoTalla = null) => {
        setLoading(true);
        setError(null);

        try {
            let url = `${baseURL}/tallasGet.php`;
            if (idTipoTalla) {
                url += `?idTipoTalla=${idTipoTalla}`;
            }

            const response = await fetch(url);
            const data = await response.json();

            if (data.success) {
                const tallasFiltradas = soloActivas
                    ? data.tallas.filter(talla => talla.activo === 1 || talla.activo === '1')
                    : data.tallas;

                // Ordenar por orden y luego por nombre
                tallasFiltradas.sort((a, b) => {
                    if (a.orden !== b.orden) {
                        return a.orden - b.orden;
                    }
                    return a.nombre.localeCompare(b.nombre);
                });

                setTallas(tallasFiltradas);
                return tallasFiltradas;
            } else {
                throw new Error(data.error || 'Error al cargar tallas');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error cargando tallas:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Obtener tallas por tipo
    const obtenerTallasPorTipo = useCallback(async (idTipoTalla) => {
        return await cargarTallas(true, idTipoTalla);
    }, [cargarTallas]);

    // Agregar una nueva talla
    const agregarTalla = useCallback(async (nombre, descripcion = '', orden = 0, idTipoTalla = null) => {
        try {
            const response = await fetch(`${baseURL}/tallasPost.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ nombre, descripcion, orden, idTipoTalla })
            });

            const data = await response.json();

            if (data.success) {
                await cargarTallas(); // Recargar la lista
                return { success: true, mensaje: data.mensaje };
            } else {
                return { success: false, error: data.error };
            }
        } catch (err) {
            return { success: false, error: 'Error de conexión' };
        }
    }, [cargarTallas]);

    // Actualizar una talla
    const actualizarTalla = useCallback(async (idTalla, updates) => {
        try {
            const response = await fetch(`${baseURL}/tallasPut.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idTalla, ...updates })
            });

            const data = await response.json();

            if (data.success) {
                await cargarTallas(); // Recargar la lista
                return { success: true, mensaje: data.mensaje };
            } else {
                return { success: false, error: data.error };
            }
        } catch (err) {
            return { success: false, error: 'Error de conexión' };
        }
    }, [cargarTallas]);

    // Eliminar una talla
    const eliminarTalla = useCallback(async (idTalla) => {
        try {
            const response = await fetch(`${baseURL}/tallasDelete.php?idTalla=${idTalla}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                await cargarTallas(); // Recargar la lista
                return { success: true, mensaje: data.mensaje };
            } else {
                return { success: false, error: data.error };
            }
        } catch (err) {
            return { success: false, error: 'Error de conexión' };
        }
    }, [cargarTallas]);

    // Agregar un nuevo tipo de talla
    const agregarTipoTalla = useCallback(async (codigo, nombre, descripcion = '') => {
        try {
            const response = await fetch(`${baseURL}/tiposTallasPost.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ codigo, nombre, descripcion })
            });

            const data = await response.json();

            if (data.success) {
                await cargarTiposTallas(); // Recargar la lista
                return { success: true, mensaje: data.mensaje };
            } else {
                return { success: false, error: data.error };
            }
        } catch (err) {
            return { success: false, error: 'Error de conexión' };
        }
    }, [cargarTiposTallas]);

    // Actualizar un tipo de talla
    const actualizarTipoTalla = useCallback(async (idTipoTalla, updates) => {
        try {
            const response = await fetch(`${baseURL}/tiposTallasPut.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idTipoTalla, ...updates })
            });

            const data = await response.json();

            if (data.success) {
                await cargarTiposTallas(); // Recargar la lista
                return { success: true, mensaje: data.mensaje };
            } else {
                return { success: false, error: data.error };
            }
        } catch (err) {
            return { success: false, error: 'Error de conexión' };
        }
    }, [cargarTiposTallas]);

    // Eliminar un tipo de talla
    const eliminarTipoTalla = useCallback(async (idTipoTalla) => {
        try {
            const response = await fetch(`${baseURL}/tiposTallasDelete.php?idTipoTalla=${idTipoTalla}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                await cargarTiposTallas(); // Recargar la lista
                await cargarTallas(); // También recargar tallas
                return { success: true, mensaje: data.mensaje };
            } else {
                return { success: false, error: data.error };
            }
        } catch (err) {
            return { success: false, error: 'Error de conexión' };
        }
    }, [cargarTiposTallas, cargarTallas]);

    // Obtener una talla por nombre
    const obtenerTallaPorNombre = useCallback((nombre) => {
        return tallas.find(talla =>
            talla.nombre.toLowerCase() === nombre.toLowerCase()
        );
    }, [tallas]);

    // Obtener tallas activas solamente
    const tallasActivas = tallas.filter(talla => talla.activo === 1 || talla.activo === '1');

    // Obtener tallas como array de strings (para compatibilidad con el código existente)
    const tallasComoStrings = tallasActivas.map(talla => talla.nombre);

    // Cargar tallas y tipos al montar el hook
    useEffect(() => {
        cargarTiposTallas();
        cargarTallas();
    }, [cargarTallas, cargarTiposTallas]);

    return {
        tallas,
        tiposTallas,
        tallasActivas,
        tallasComoStrings,
        loading,
        error,
        cargarTallas,
        cargarTiposTallas,
        obtenerTallasPorTipo,
        agregarTalla,
        actualizarTalla,
        eliminarTalla,
        obtenerTallaPorNombre,
        agregarTipoTalla,
        actualizarTipoTalla,
        eliminarTipoTalla
    };
};